import type {
  Comment,
  Cue,
  Id,
  PresenceUser,
  ScriptColumn,
  ScriptDocument,
} from "@supermanus/document-model";

export const PROTOCOL_VERSION = 2 as const;

export type ClientToServerMessage =
  | { type: "auth"; token: string; displayName: string; userId: Id }
  | {
      type: "join_project";
      projectId: Id;
      document?: ScriptDocument;
    }
  | { type: "leave_project"; projectId: Id }
  | {
      type: "presence";
      projectId: Id;
      episodeId?: Id;
      sceneId?: Id;
      cueId?: Id;
      column?: ScriptColumn;
    }
  | {
      type: "lock.acquire";
      projectId: Id;
      cueId: Id;
      column: ScriptColumn;
      expectedRevision: number;
    }
  | {
      type: "lock.release";
      projectId: Id;
      cueId: Id;
      column: ScriptColumn;
    }
  | {
      type: "lock.heartbeat";
      projectId: Id;
      cueId: Id;
      column: ScriptColumn;
    }
  | {
      type: "cue.upsert";
      projectId: Id;
      cue: Cue;
      /** Revision of the side being edited, for conflict detection. */
      column: ScriptColumn;
      baseRevision: number;
    }
  | {
      type: "cue.move";
      projectId: Id;
      sceneId: Id;
      cueId: Id;
      toIndex: number;
    }
  | {
      type: "comment.create";
      projectId: Id;
      comment: Omit<Comment, "id" | "createdAt" | "resolved"> & {
        id?: Id;
      };
    }
  | { type: "sync.request"; projectId: Id; sinceRevision?: number };

export type ServerToClientMessage =
  | { type: "auth.ok"; userId: Id; displayName: string }
  | { type: "auth.error"; message: string }
  | {
      type: "project.snapshot";
      projectId: Id;
      document: ScriptDocument;
      presence: PresenceUser[];
    }
  | { type: "presence.update"; projectId: Id; users: PresenceUser[] }
  | {
      type: "lock.granted";
      projectId: Id;
      cueId: Id;
      column: ScriptColumn;
      lockOwnerId: Id;
      lockOwnerName: string;
      lockExpiresAt: string;
    }
  | {
      type: "lock.denied";
      projectId: Id;
      cueId: Id;
      column: ScriptColumn;
      reason: string;
      lockOwnerId?: Id;
      lockOwnerName?: string;
    }
  | {
      type: "lock.released";
      projectId: Id;
      cueId: Id;
      column: ScriptColumn;
    }
  | {
      type: "cue.updated";
      projectId: Id;
      cue: Cue;
    }
  | {
      type: "cue.moved";
      projectId: Id;
      sceneId: Id;
      cueIds: Id[];
    }
  | {
      type: "cue.conflict";
      projectId: Id;
      cueId: Id;
      column: ScriptColumn;
      serverCue: Cue;
      orphanContent: string;
    }
  | {
      type: "comment.created";
      projectId: Id;
      comment: Comment;
    }
  | { type: "error"; message: string; code?: string };

export type SyncMessage = ClientToServerMessage | ServerToClientMessage;

export function encodeMessage(msg: SyncMessage): string {
  return JSON.stringify({ v: PROTOCOL_VERSION, ...msg });
}

export function decodeMessage(raw: string): SyncMessage {
  const parsed = JSON.parse(raw) as SyncMessage & { v?: number };
  if (parsed.v != null && parsed.v !== PROTOCOL_VERSION) {
    throw new Error(`Unsupported protocol version: ${parsed.v}`);
  }
  const { v: _v, ...msg } = parsed as SyncMessage & { v?: number };
  return msg as SyncMessage;
}

export const POLL_INTERVAL_MS = 2_000;
/** Drop hung fetches after sleep / flaky Wi-Fi so polling can resume. */
const REQUEST_TIMEOUT_MS = 8_000;

export type SyncRevisionTracker = {
  getRevision(projectId: Id): number;
  setRevision(projectId: Id, revision: number): void;
};

type ApiErrorBody = {
  code?: string;
  error?: string;
  message?: string;
  cueId?: Id;
  column?: ScriptColumn;
  serverCue?: Cue;
  orphanContent?: string;
  reason?: string;
  lockOwnerId?: Id;
  lockOwnerName?: string;
  revision?: number;
  cue?: Cue;
  document?: ScriptDocument;
  granted?: {
    cueId: Id;
    column: ScriptColumn;
    lockOwnerId: Id;
    lockOwnerName: string;
    lockExpiresAt: string;
  };
  released?: { cueId: Id; column: ScriptColumn };
  cueIds?: Id[];
  sceneId?: Id;
  presence?: PresenceUser[];
};

async function apiRequest<T>(
  apiBase: string,
  path: string,
  init?: RequestInit,
): Promise<{ ok: true; status: number; data: T } | { ok: false; status: number; data: ApiErrorBody }> {
  let res: Response;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    res = await fetch(`${apiBase.replace(/\/$/, "")}${path}`, {
      credentials: "include",
      ...init,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    return {
      ok: false,
      status: 0,
      data: { code: "network", error: "Ingen forbindelse" },
    };
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text();
  let data: unknown = {};
  if (text) {
    try {
      data = JSON.parse(text) as unknown;
    } catch {
      data = { error: text };
    }
  }
  if (!res.ok) {
    return { ok: false, status: res.status, data: data as ApiErrorBody };
  }
  return { ok: true, status: res.status, data: data as T };
}

function isNetworkFailure(
  res: { ok: boolean; status: number; data: ApiErrorBody },
): boolean {
  return !res.ok && (res.status === 0 || res.data.code === "network");
}

/**
 * HTTP + polling stand-in for {@link SyncClient} (no WebSocket).
 * Speaks the same message types so the editor app can stay unchanged.
 */
export class HttpSyncClient {
  private listeners = new Set<(msg: ServerToClientMessage) => void>();
  private apiBase: string;
  private revisions?: SyncRevisionTracker;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private pollInFlight = false;
  /** Wall-clock start of the current poll — used to abandon hung fetches after sleep. */
  private pollStartedAt = 0;
  private pollGeneration = 0;
  private projectId: Id | null = null;
  private userId?: Id;
  private alive = false;
  /** One in-flight cue.upsert chain per cell — prevents baseRevision races. */
  private cueUpsertTail = new Map<string, Promise<void>>();
  private lastAuth: Extract<ClientToServerMessage, { type: "auth" }> | null =
    null;
  private recoveringSession = false;

  constructor(apiBase = "/api", revisions?: SyncRevisionTracker) {
    this.apiBase = apiBase;
    this.revisions = revisions;
  }

  connect(): Promise<void> {
    this.alive = true;
    return Promise.resolve();
  }

  disconnect(): void {
    this.alive = false;
    this.stopPoll();
    this.cueUpsertTail.clear();
    if (this.projectId && this.userId) {
      void apiRequest(this.apiBase, `/projects/${encodeURIComponent(this.projectId)}/presence`, {
        method: "DELETE",
      });
    }
    this.projectId = null;
  }

  send(msg: ClientToServerMessage): boolean {
    if (!this.alive) return false;
    if (msg.type === "auth") {
      this.lastAuth = msg;
    }
    if (msg.type === "cue.upsert") {
      this.enqueueCueUpsert(msg);
      return true;
    }
    if (msg.type === "sync.request") {
      // Forced poll after wake/focus — must not be blocked by a hung pre-sleep fetch.
      void this.poll(msg.projectId, true);
      return true;
    }
    void this.dispatch(msg);
    return true;
  }

  /** Re-establish session cookie after sleep / expiry, then poll. */
  async recoverSession(projectId: Id): Promise<boolean> {
    if (!this.alive || !this.lastAuth || this.recoveringSession) return false;
    this.recoveringSession = true;
    try {
      await this.dispatch(this.lastAuth);
      if (this.projectId !== projectId) {
        await this.dispatch({
          type: "join_project",
          projectId,
        });
      }
      await this.poll(projectId, true);
      return true;
    } finally {
      this.recoveringSession = false;
    }
  }

  /**
   * Run cue upserts for the same cell strictly in order. Overlapping POSTs
   * otherwise race on baseRevision and the conflict handler can wipe newer text.
   */
  private enqueueCueUpsert(
    msg: Extract<ClientToServerMessage, { type: "cue.upsert" }>,
  ): void {
    const key = `${msg.projectId}:${msg.cue.id}:${msg.column}`;
    const prev = this.cueUpsertTail.get(key) ?? Promise.resolve();
    const next = prev
      .catch(() => undefined)
      .then(async () => {
        if (!this.alive) return;
        await this.dispatch(msg);
      });
    this.cueUpsertTail.set(
      key,
      next.finally(() => {
        if (this.cueUpsertTail.get(key) === next) this.cueUpsertTail.delete(key);
      }),
    );
  }

  onMessage(listener: (msg: ServerToClientMessage) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get connected(): boolean {
    return this.alive;
  }

  private emit(msg: ServerToClientMessage): void {
    for (const listener of this.listeners) listener(msg);
  }

  private setRev(projectId: Id, revision: number | undefined): void {
    if (revision != null) this.revisions?.setRevision(projectId, revision);
  }

  private stopPoll(): void {
    if (this.pollTimer != null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private startPoll(projectId: Id): void {
    this.stopPoll();
    this.pollTimer = setInterval(() => {
      void this.poll(projectId);
    }, POLL_INTERVAL_MS);
    void this.poll(projectId);
  }

  private emitNetworkError(): void {
    this.emit({
      type: "error",
      message: "Ingen forbindelse",
      code: "network",
    });
  }

  private async poll(projectId: Id, force = false): Promise<void> {
    if (!this.alive || this.projectId !== projectId) return;
    const staleMs = REQUEST_TIMEOUT_MS + 2_000;
    if (this.pollInFlight) {
      const age = Date.now() - this.pollStartedAt;
      // After laptop sleep a fetch can hang until abort — don't block recovery
      // probes forever. Force (sync.request) always starts a fresh poll.
      if (!force && age < staleMs) return;
    }
    const gen = ++this.pollGeneration;
    this.pollInFlight = true;
    this.pollStartedAt = Date.now();
    try {
      const since = this.revisions?.getRevision(projectId) ?? 0;
      const res = await apiRequest<{
        revision: number;
        presence: PresenceUser[];
        document?: ScriptDocument;
      }>(this.apiBase, `/projects/${encodeURIComponent(projectId)}/sync?since=${since}`);
      if (gen !== this.pollGeneration) return;
      if (!res.ok) {
        if (isNetworkFailure(res)) {
          this.emitNetworkError();
          return;
        }
        if (res.status === 401) {
          this.emit({ type: "error", message: "Not authenticated", code: "unauthenticated" });
        }
        return;
      }
      this.setRev(projectId, res.data.revision);
      this.emit({
        type: "presence.update",
        projectId,
        users: res.data.presence ?? [],
      });
      if (res.data.document) {
        this.emit({
          type: "project.snapshot",
          projectId,
          document: res.data.document,
          presence: res.data.presence ?? [],
        });
      }
    } finally {
      if (gen === this.pollGeneration) {
        this.pollInFlight = false;
      }
    }
  }

  private async dispatch(msg: ClientToServerMessage): Promise<void> {
    try {
      switch (msg.type) {
        case "auth": {
          const res = await apiRequest<{
            user: { userId: Id; displayName: string };
          }>(this.apiBase, "/session", {
            method: "POST",
            body: JSON.stringify({
              licenseKey: msg.token,
              displayName: msg.displayName,
              userId: msg.userId,
            }),
          });
          if (!res.ok) {
            if (isNetworkFailure(res)) {
              this.emitNetworkError();
              return;
            }
            this.emit({
              type: "auth.error",
              message: res.data.error || "Authentication failed",
            });
            return;
          }
          this.userId = res.data.user.userId;
          this.emit({
            type: "auth.ok",
            userId: res.data.user.userId,
            displayName: res.data.user.displayName,
          });
          return;
        }
        case "join_project": {
          this.projectId = msg.projectId;
          let row = await apiRequest<{
            revision: number;
            document: ScriptDocument;
          }>(this.apiBase, `/projects/${encodeURIComponent(msg.projectId)}`);
          if (!row.ok && row.status === 404 && msg.document) {
            row = await apiRequest<{
              revision: number;
              document: ScriptDocument;
            }>(this.apiBase, "/projects", {
              method: "POST",
              body: JSON.stringify({ document: msg.document }),
            });
          }
          if (!row.ok || !("document" in row.data)) {
            if (isNetworkFailure(row as { ok: boolean; status: number; data: ApiErrorBody })) {
              this.emitNetworkError();
              this.startPoll(msg.projectId);
              return;
            }
            this.emit({
              type: "error",
              message: row.ok ? "Project not found" : row.data.error || "Project not found",
            });
            return;
          }
          this.setRev(msg.projectId, row.data.revision);
          const presenceRes = await apiRequest<{ presence: PresenceUser[] }>(
            this.apiBase,
            `/projects/${encodeURIComponent(msg.projectId)}/presence`,
            { method: "POST", body: JSON.stringify({}) },
          );
          const presence = presenceRes.ok ? presenceRes.data.presence ?? [] : [];
          this.emit({
            type: "project.snapshot",
            projectId: msg.projectId,
            document: row.data.document,
            presence,
          });
          this.startPoll(msg.projectId);
          return;
        }
        case "leave_project": {
          this.stopPoll();
          await apiRequest(
            this.apiBase,
            `/projects/${encodeURIComponent(msg.projectId)}/presence`,
            { method: "DELETE" },
          );
          if (this.projectId === msg.projectId) this.projectId = null;
          return;
        }
        case "presence": {
          const res = await apiRequest<{ presence: PresenceUser[] }>(
            this.apiBase,
            `/projects/${encodeURIComponent(msg.projectId)}/presence`,
            {
              method: "POST",
              body: JSON.stringify({
                episodeId: msg.episodeId,
                sceneId: msg.sceneId,
                cueId: msg.cueId,
                column: msg.column,
              }),
            },
          );
          if (res.ok) {
            this.emit({
              type: "presence.update",
              projectId: msg.projectId,
              users: res.data.presence ?? [],
            });
          }
          return;
        }
        case "lock.acquire":
        case "lock.release":
        case "lock.heartbeat": {
          const action =
            msg.type === "lock.acquire"
              ? "acquire"
              : msg.type === "lock.release"
                ? "release"
                : "heartbeat";
          const res = await apiRequest<ApiErrorBody>(
            this.apiBase,
            `/projects/${encodeURIComponent(msg.projectId)}/locks`,
            {
              method: "POST",
              body: JSON.stringify({
                action,
                cueId: msg.cueId,
                column: msg.column,
              }),
            },
          );
          if (!res.ok) {
            if (isNetworkFailure(res)) {
              this.emitNetworkError();
              return;
            }
            this.emit({
              type: "lock.denied",
              projectId: msg.projectId,
              cueId: msg.cueId,
              column: msg.column,
              reason: res.data.reason || res.data.error || "Lock denied",
              lockOwnerId: res.data.lockOwnerId,
              lockOwnerName: res.data.lockOwnerName ?? undefined,
            });
            return;
          }
          this.setRev(msg.projectId, res.data.revision);
          if (res.data.granted) {
            this.emit({
              type: "lock.granted",
              projectId: msg.projectId,
              ...res.data.granted,
            });
          }
          if (res.data.released) {
            this.emit({
              type: "lock.released",
              projectId: msg.projectId,
              ...res.data.released,
            });
          }
          return;
        }
        case "cue.upsert": {
          const res = await apiRequest<ApiErrorBody>(
            this.apiBase,
            `/projects/${encodeURIComponent(msg.projectId)}/cues`,
            {
              method: "POST",
              body: JSON.stringify({
                cue: msg.cue,
                column: msg.column,
                baseRevision: msg.baseRevision,
              }),
            },
          );
          if (!res.ok) {
            if (isNetworkFailure(res)) {
              this.emitNetworkError();
              return;
            }
            if (res.data.code === "cue_conflict" && res.data.serverCue) {
              this.emit({
                type: "cue.conflict",
                projectId: msg.projectId,
                cueId: msg.cue.id,
                column: msg.column,
                serverCue: res.data.serverCue,
                orphanContent: res.data.orphanContent ?? "",
              });
              return;
            }
            if (res.data.code === "lock_denied") {
              this.emit({
                type: "lock.denied",
                projectId: msg.projectId,
                cueId: msg.cue.id,
                column: msg.column,
                reason: res.data.reason || "Cannot edit locked cell",
                lockOwnerId: res.data.lockOwnerId,
                lockOwnerName: res.data.lockOwnerName ?? undefined,
              });
              return;
            }
            this.emit({
              type: "error",
              message: res.data.error || "Cue update failed",
              code: res.data.code,
            });
            return;
          }
          this.setRev(msg.projectId, res.data.revision);
          if (res.data.cue) {
            this.emit({
              type: "cue.updated",
              projectId: msg.projectId,
              cue: res.data.cue,
            });
          }
          return;
        }
        case "cue.move": {
          const res = await apiRequest<ApiErrorBody>(
            this.apiBase,
            `/projects/${encodeURIComponent(msg.projectId)}/cues/move`,
            {
              method: "POST",
              body: JSON.stringify({
                sceneId: msg.sceneId,
                cueId: msg.cueId,
                toIndex: msg.toIndex,
              }),
            },
          );
          if (!res.ok) {
            if (isNetworkFailure(res)) {
              this.emitNetworkError();
              return;
            }
            this.emit({
              type: "error",
              message: res.data.error || "Move failed",
            });
            return;
          }
          this.setRev(msg.projectId, res.data.revision);
          if (res.data.cueIds) {
            this.emit({
              type: "cue.moved",
              projectId: msg.projectId,
              sceneId: msg.sceneId,
              cueIds: res.data.cueIds,
            });
          }
          return;
        }
        case "comment.create": {
          this.emit({
            type: "comment.created",
            projectId: msg.projectId,
            comment: {
              id: msg.comment.id ?? crypto.randomUUID(),
              cueId: msg.comment.cueId,
              column: msg.comment.column,
              authorId: msg.comment.authorId,
              authorName: msg.comment.authorName,
              body: msg.comment.body,
              createdAt: new Date().toISOString(),
              resolved: false,
            },
          });
          return;
        }
        case "sync.request": {
          await this.poll(msg.projectId, true);
          return;
        }
      }
    } catch (err) {
      const network =
        err instanceof TypeError ||
        (err instanceof Error && /failed to fetch|networkerror/i.test(err.message));
      this.emit({
        type: "error",
        message: err instanceof Error ? err.message : "Sync request failed",
        code: network ? "network" : "sync",
      });
    }
  }
}

export class SyncClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<(msg: ServerToClientMessage) => void>();
  private url: string;

  constructor(url: string) {
    this.url = url;
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(this.url);
      this.ws = ws;
      ws.onopen = () => resolve();
      ws.onerror = () => reject(new Error("WebSocket connection failed"));
      ws.onmessage = (ev) => {
        try {
          const msg = decodeMessage(String(ev.data)) as ServerToClientMessage;
          for (const listener of this.listeners) listener(msg);
        } catch (err) {
          console.error("Failed to decode sync message", err);
        }
      };
      ws.onclose = () => {
        this.ws = null;
      };
    });
  }

  disconnect(): void {
    this.ws?.close();
    this.ws = null;
  }

  send(msg: ClientToServerMessage): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false;
    }
    this.ws.send(encodeMessage(msg));
    return true;
  }

  onMessage(listener: (msg: ServerToClientMessage) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}
